import { Flex, Heading, Text } from "@radix-ui/themes";
import { usePostHog } from "@posthog/react";
import {
  Component,
  Fragment,
  type ErrorInfo,
  type ReactNode,
  useEffect,
} from "react";

const CRASH_GUARD_KEY = "kiosk_render_crash_guard_v1";
const CRASH_WINDOW_MS = 10 * 60_000;
const MAX_AUTOMATIC_REMOUNTS = 3;
const MAX_ERROR_MESSAGE_LENGTH = 500;
const MAX_ERROR_CONTEXT_LENGTH = 2_000;

function sanitizeErrorText(
  value: unknown,
  maxLength: number,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const redacted = trimmed
    .replace(/\bBearer\s+[^\s,;]+/gi, "Bearer [REDACTED]")
    .replace(
      /([?&](?:access_token|refresh_token|token|code|api_key)=)[^&#\s]*/gi,
      "$1[REDACTED]",
    )
    .replace(
      /((?:password|secret|authorization)\s*[:=]\s*)[^\s,;]+/gi,
      "$1[REDACTED]",
    );

  return redacted.length > maxLength
    ? `${redacted.slice(0, maxLength - 1)}…`
    : redacted;
}

function errorDetails(error: unknown, fallbackMessage?: string) {
  return {
    error_name: error instanceof Error ? error.name : "UnknownError",
    error_message: sanitizeErrorText(
      error instanceof Error ? error.message : fallbackMessage,
      MAX_ERROR_MESSAGE_LENGTH,
    ),
    error_stack:
      error instanceof Error
        ? sanitizeErrorText(error.stack, MAX_ERROR_CONTEXT_LENGTH)
        : undefined,
  };
}

function createIncidentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `incident-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getIncidentContext() {
  try {
    const session = JSON.parse(localStorage.getItem("user") || "null") as {
      staff?: { userId?: string; fullName?: string };
    } | null;
    const binding = JSON.parse(
      localStorage.getItem("kiosk_binding_v1") || "null",
    ) as {
      kioskInstallationId?: string;
      // Read-only compatibility for bindings created before kiosk v2 shipped.
      kioskId?: string;
      storeKey?: string;
      zoneId?: string;
      placement?: string;
    } | null;
    return {
      kiosk_installation_id:
        binding?.kioskInstallationId ?? binding?.kioskId,
      store_key: binding?.storeKey,
      zone_id: binding?.zoneId,
      placement: binding?.placement,
      user_id: session?.staff?.userId,
      user_full_name: session?.staff?.fullName,
    };
  } catch {
    return {};
  }
}

function incidentProperties(code: string, operation: string) {
  return {
    lifecycle: "opened",
    severity: "critical",
    incident_id: createIncidentId(),
    incident_code: code,
    operation,
    app_version: import.meta.env.VITE_APP_VERSION,
    online: navigator.onLine,
    ...getIncidentContext(),
  };
}

function recordCrash() {
  const now = Date.now();
  let timestamps: number[] = [];
  try {
    const parsed = JSON.parse(
      localStorage.getItem(CRASH_GUARD_KEY) || "null",
    ) as { timestamps?: number[] } | null;
    if (Array.isArray(parsed?.timestamps)) timestamps = parsed.timestamps;
  } catch {
    // A missing guard should never prevent the boundary from rendering.
  }
  timestamps = timestamps.filter(
    (timestamp) => now - timestamp < CRASH_WINDOW_MS,
  );
  timestamps.push(now);
  try {
    localStorage.setItem(CRASH_GUARD_KEY, JSON.stringify({ timestamps }));
  } catch {
    // Best effort in Safari private mode.
  }
  return timestamps.length;
}

class KioskErrorBoundary extends Component<
  {
    children: ReactNode;
    onCrash: (error: Error, info: ErrorInfo, count: number) => void;
  },
  { error: Error | null; recoveryKey: number; crashCount: number }
> {
  state = { error: null, recoveryKey: 0, crashCount: 0 };
  private recoveryTimer?: number;

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const crashCount = recordCrash();
    this.setState({ crashCount });
    this.props.onCrash(error, info, crashCount);
    if (crashCount < MAX_AUTOMATIC_REMOUNTS) {
      this.recoveryTimer = window.setTimeout(() => {
        this.setState((state) => ({
          error: null,
          recoveryKey: state.recoveryKey + 1,
        }));
      }, 1_000);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.recoveryTimer);
  }

  render() {
    if (this.state.error) {
      return (
        <main className="h-dvh bg-white p-6">
          <Flex
            direction="column"
            align="center"
            justify="center"
            className="h-full"
            gap="3"
          >
            <img src="/warning.webp" className="size-[190px]" alt="" />
            <Heading size="9">Temporarily out of service</Heading>
            <Text size="6" color="gray">
              {this.state.crashCount < MAX_AUTOMATIC_REMOUNTS
                ? "The kiosk is recovering automatically."
                : "Remote support has been notified."}
            </Text>
          </Flex>
        </main>
      );
    }
    return (
      <Fragment key={this.state.recoveryKey}>{this.props.children}</Fragment>
    );
  }
}

export function KioskErrorBoundaryReporter({
  children,
}: {
  children: ReactNode;
}) {
  const posthog = usePostHog();

  useEffect(() => {
    const captureGlobalError = (event: ErrorEvent) => {
      posthog.capture("kiosk_incident", {
        ...incidentProperties("unhandled_error", "window_error"),
        ...errorDetails(event.error, event.message),
        source_file: sanitizeErrorText(
          event.filename,
          MAX_ERROR_MESSAGE_LENGTH,
        ),
        source_line: event.lineno || undefined,
        source_column: event.colno || undefined,
      });
    };
    const captureRejection = (event: PromiseRejectionEvent) => {
      posthog.capture("kiosk_incident", {
        ...incidentProperties("unhandled_rejection", "unhandled_rejection"),
        ...errorDetails(
          event.reason,
          typeof event.reason === "string" ? event.reason : undefined,
        ),
      });
    };
    window.addEventListener("error", captureGlobalError);
    window.addEventListener("unhandledrejection", captureRejection);
    return () => {
      window.removeEventListener("error", captureGlobalError);
      window.removeEventListener("unhandledrejection", captureRejection);
    };
  }, [posthog]);

  return (
    <KioskErrorBoundary
      onCrash={(error, info, count) => {
        posthog.capture("kiosk_incident", {
          ...incidentProperties(
            count >= MAX_AUTOMATIC_REMOUNTS
              ? "render_crash_loop"
              : "render_crash",
            "react_render",
          ),
          occurrence_count: count,
          ...errorDetails(error),
          component_stack: sanitizeErrorText(
            info.componentStack,
            MAX_ERROR_CONTEXT_LENGTH,
          ),
        });
      }}
    >
      {children}
    </KioskErrorBoundary>
  );
}
